import { useEffect, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import SuccessModal from "../../../common/SuccessModal";
import DeleteModal from "../../../common/DeleteModal";
import BackButton from "../../../common/BackButton";
import Loader from "../../../common/Loader";
import TableError from "../../common/TableError";
import "../Teacher/Teacher.css";
import { useGetStudent } from "../../../../hooks/school/student/useGetStudent";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useUpdateStudent } from "../../../../hooks/school/student/useUpdateStudent";
import { useDeleteStudent } from "../../../../hooks/school/student/useDeleteStudent";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import { createStudentValidationSchema } from "./CreateStudent";
import { Button } from "react-bootstrap";
import { useToggleStudentStatus } from "../../../../hooks/school/student/useToggleStudentStatus";
import "../../SuperAdmin/toggle.css";

const EditStudent = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const { student, loading, error, getStudent } = useGetStudent();
  const {
    updateStudent,
    loading: updating,
    error: updateError,
  } = useUpdateStudent();
  const {
    deleteStudent,
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
    resetState: resetDeleteState,
  } = useDeleteStudent();
  const {
    teachers,
    metaData,
    getTeachers,
    loading: teachersLoading,
    error: teachersError,
  } = useGetTeachers();
  const {
    toggleStudentStatus,
    loading: toggleLoading,
    error: toggleError,
    success: toggleSuccess,
    resetState: resetToggleState,
  } = useToggleStudentStatus();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTimerRef = useRef(null);

  const isActive = student?.status === "1" || student?.status === 1;

  const fullName = student?.name || "";

  const handleShow = () => {
    resetDeleteState();
    setShowModal(true);
  };

  const handleClose = () => {
    resetDeleteState();
    setShowModal(false);
  };

  useEffect(() => {
    getStudent({ studentId });
  }, [studentId]);

  // Load teachers on mount
  useEffect(() => {
    getTeachers(1, ITEMS_PER_PAGE, false);
  }, []);

  const initialValues = student
    ? {
        firstName: student?.name?.split(" ")[0] || "",
        lastName: student?.name?.split(" ")[1] || "",
        username: student?.username || "",
        assigned_to: student?.assigned_teacher?.id || "",
        parent_email: student?.email || "",
        age: student?.age || "",
      }
    : {
        firstName: "",
        lastName: "",
        username: "",
        assigned_to: "",
        parent_email: "",
        age: "",
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const updatedData = {
        name: `${values.firstName} ${values.lastName}`,
        username: values.username,
        assigned_to: Number(values.assigned_to) || null,
        parent_email: values.parent_email,
        age: values.age,
      };

      await updateStudent({
        studentId,
        updatedData,
        onSuccess: () => {
          setShowSuccessModal(true);
        },
      });
    } catch (error) {
      // Error is handled by the hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToStudentList = () => {
    setShowSuccessModal(false);
    navigate("/school-admin/students");
  };

  const handleContinueEditing = () => {
    setShowSuccessModal(false);
  };

  const handleDeleteStudent = async () => {
    await deleteStudent(studentId);
  };

  // Loading state
  if (loading) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="EDIT STUDENT" />
        <Loader message="Loading Student Details..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="EDIT STUDENT" />
        <TableError
          error={error}
          handleRefresh={() => getStudent({ studentId })}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="pb-2">
        <BackButton />
      </div>
      <TopImage imageSrc={Images.TopImage} text="EDIT STUDENT" />

      <div className="d-flex justify-content-end align-items-center pt-3 pb-3 gap-3">
        {/* Status Toggle */}
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
          <div className="toggle-switch">
            <input
              type="checkbox"
              id={`switch-${student?.id}`}
              checked={isActive}
              onChange={() => {
                resetToggleState();
                setShowToggleModal(true);
              }}
            />
            <label
              htmlFor={`switch-${student?.id}`}
              className="slider"
              style={{
                backgroundColor: isActive ? "#008EF4" : "",
              }}
            ></label>
          </div>
        </div>

        <div
          className="text-decoration-underline"
          style={{ cursor: "pointer", color: "var(--text-color)" }}
        >
          <Link onClick={() => handleShow()}>Delete Student</Link>

          <DeleteModal
            showModal={showModal}
            handleClose={handleClose}
            message="Are you sure you want to delete this Student? This action cannot be undone."
            loading={deleteLoading}
            handleDelete={handleDeleteStudent}
            error={deleteError}
            success={deleteSuccess}
            successMessage="Student has been deleted successfully."
            onSuccessContinue={() => navigate("/school-admin/students")}
          />
        </div>
      </div>

      <DeleteModal
        showModal={showToggleModal}
        handleClose={() => {
          resetToggleState();
          setShowToggleModal(false);
        }}
        handleDelete={async () => {
          await toggleStudentStatus(studentId);
        }}
        message={`Are you sure you want to ${isActive ? "deactivate" : "activate"} ${student?.name || "this student"}?`}
        loading={toggleLoading}
        error={toggleError}
        success={toggleSuccess}
        successMessage={`${student?.name || "Student"} has been ${isActive ? "deactivated" : "activated"} successfully.`}
        onSuccessContinue={() => {
          setShowToggleModal(false);
          resetToggleState();
          getStudent({ studentId });
        }}
        customSuccessTitle={
          isActive ? "Deactivated Successfully" : "Activated Successfully"
        }
        customErrorTitle="Toggle Failed"
        customConfirmTitle="Confirm Status Change"
        customActionButtonText={isActive ? "Deactivate" : "Activate"}
        customLoadingButtonText={isActive ? "Deactivating..." : "Activating..."}
      />

      <div className="custom-card">
        <Formik
          initialValues={initialValues}
          validationSchema={createStudentValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div className="row">
                <div className="col-md-6">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <CustomInput
                    label="Student ID"
                    name="username"
                    type="text"
                    placeholder="Enter student ID"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Age"
                    name="age"
                    type="string"
                    placeholder="Enter Age"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <label className="mb-1">Assigned Teacher</label>
                  <div className="scroll-wrapper">
                    <Field name="assigned_to">
                      {({ field, form }) => (
                        <div className="custom-dropdown-container">
                          {/* Selected Box */}
                          <div
                            className="custom-dropdown-select custom-primary-input"
                            tabIndex={0}
                            role="button"
                            onClick={() => {
                              if (!dropdownOpen) {
                                setSearchTerm("");
                                setPage(1);
                                getTeachers(1, ITEMS_PER_PAGE, false);
                              }
                              setDropdownOpen(!dropdownOpen);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (!dropdownOpen) {
                                  setSearchTerm("");
                                  setPage(1);
                                  getTeachers(1, ITEMS_PER_PAGE, false);
                                }
                                setDropdownOpen(!dropdownOpen);
                              }
                            }}
                          >
                            {teachers?.find((t) => t.id === field.value)
                              ? `${
                                  teachers.find((t) => t.id === field.value)
                                    .firstName
                                } ${
                                  teachers.find((t) => t.id === field.value)
                                    .lastName
                                }`
                              : student?.assigned_teacher
                                ? `${student.assigned_teacher.firstName} ${student.assigned_teacher.lastName}`
                                : "Select Teacher"}
                          </div>

                          {/* Options List */}
                          {dropdownOpen && (
                            <div className="custom-dropdown-options">
                              {/* Search Input */}
                              <div className="custom-dropdown-search">
                                <input
                                  type="text"
                                  className="custom-dropdown-search-input"
                                  placeholder="Search teacher..."
                                  value={searchTerm}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Tab") {
                                      setDropdownOpen(false);
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchTerm(value);
                                    clearTimeout(searchTimerRef.current);
                                    searchTimerRef.current = setTimeout(() => {
                                      setPage(1);
                                      getTeachers(
                                        1,
                                        ITEMS_PER_PAGE,
                                        false,
                                        value,
                                      );
                                    }, 300);
                                  }}
                                />
                              </div>

                              <div
                                className="custom-dropdown-scroll-area"
                                onScroll={(e) => {
                                  const bottomReached =
                                    e.target.scrollTop +
                                      e.target.clientHeight >=
                                    e.target.scrollHeight - 2;

                                  const hasMore =
                                    metaData?.current_page <
                                    metaData?.last_page;

                                  if (
                                    bottomReached &&
                                    hasMore &&
                                    !teachersLoading
                                  ) {
                                    const next = page + 1;
                                    setPage(next);
                                    getTeachers(
                                      next,
                                      ITEMS_PER_PAGE,
                                      true,
                                      searchTerm,
                                    );
                                  }
                                }}
                              >
                                {/* Loading State */}
                                {teachersLoading ? (
                                  <div className="custom-dropdown-option disabled">
                                    Loading...
                                  </div>
                                ) : (
                                  <>
                                    {/* Error State */}
                                    {teachersError && (
                                      <div className="custom-dropdown-option disabled text-danger">
                                        {teachersError}
                                      </div>
                                    )}

                                    {/* Empty State */}
                                    {!teachersError &&
                                      (!teachers || teachers.length === 0) && (
                                        <div className="custom-dropdown-option disabled">
                                          No teachers found
                                        </div>
                                      )}

                                    {/* Teacher Options */}
                                    {teachers?.map((teacher) => (
                                      <div
                                        key={teacher.id}
                                        className="custom-dropdown-option"
                                        onClick={() => {
                                          form.setFieldValue(
                                            "assigned_to",
                                            teacher.id,
                                          );
                                          setDropdownOpen(false);
                                        }}
                                      >
                                        {teacher.firstName} {teacher.lastName}
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
              </div>

              <CustomInput
                label="Email Address"
                name="parent_email"
                type="email"
                placeholder="Enter email address"
                className="custom-primary-input"
                layout="stacked"
                disabled={true}
              />

              <div>
                {updateError && (
                  <p className="custom-error pb-2">{updateError}</p>
                )}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || updating}
                >
                  {isSubmitting || updating ? "Saving..." : "Save"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Student Updated Successfully"
        message="The student details have been updated successfully."
        primaryButtonText="Continue Editing"
        secondaryButtonText="Student List"
        onPrimaryClick={handleContinueEditing}
        onSecondaryClick={handleGoToStudentList}
      />
    </div>
  );
};

export default EditStudent;
